import { useState, useRef, useEffect } from "react";
import { MdOutlineDelete } from "react-icons/md";
import UploadImage from "../../../components/molecules/UploadImage";
import TextInput from "../../../components/molecules/inputs/TextInput";
import TextAreaInput from "../../../components/molecules/inputs/TextAreaInput";
import { IoMdAdd } from "react-icons/io";
import api from "../../../Api";
import axios from "axios";
import Skeleton from "react-loading-skeleton";

interface SupportItem {
  _id?: string;
  Icon: string;
  Name: string;
  Description: string;
}

export default function OurSupport() {
  const [isPopUpOpen, SetIsPopUpOpen] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<File | string>("");
  const [inputValue, setInputValue] = useState("");
  const popUpRef = useRef<HTMLDivElement>(null);
  const [ourSupport, SetOurSupport] = useState<SupportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [popUpLoading, setPopupLoading] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState("");

  const handleImageUpload = (file: File) => {
    setUploadedImage(file); // Accept the file object
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const handleDescriptionChange = (value: string) => {
    setDescriptionValue(value);
  };

  // Handle clicks outside the popup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popUpRef.current &&
        !popUpRef.current.contains(event.target as Node)
      ) {
        SetIsPopUpOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside); // Cleanup
    };
  }, []);

  useEffect(() => {
    const fetchOurSupport = async () => {
      try {
        const response = await axios.get(api.ourSupport, {
          headers: {
            api_key: api.key,
            authentication: api.authantication,
          },
        });
        SetOurSupport(response.data.ourSupport);
      } catch (error) {
        console.error("Error fetching our Support:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOurSupport();
  }, []);

  const handleSubmit = async () => {
    if (!uploadedImage || typeof uploadedImage === "string") {
      alert("Please upload an image.");
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadedImage);

    try {
      setPopupLoading(true);
      const uploadResponse = await axios.post(api.uploadFile, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (uploadResponse.data.success) {
        const uploadedImageUrl = uploadResponse.data.url;

        const supportItem: SupportItem = {
          Icon: uploadedImageUrl,
          Name: inputValue,
          Description: descriptionValue, // Adjust if you have a separate state for the description
        };

        const response = await axios.post(api.ourSupport, supportItem, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.data.success) {
          SetOurSupport((prevSupport) => [...prevSupport, supportItem]);
          SetIsPopUpOpen(false);
          setPopupLoading(false);
        } else {
          setPopupLoading(false);
          console.warn("Failed to add support item. Please try again.");
        }
      } else {
        console.error("Failed to upload image. Please try again.");
      }
    } catch (error) {
      console.error("Error adding support item:", error);
    }
  };

  // Delete our support handler
  const handleDeleteOurSupport = async (ourSupportId: string) => {
    try {
      await axios.delete(`${api.ourSupport}/${ourSupportId}`, {
        headers: {
          api_key: api.key,
          authentication: api.authantication,
        },
      });

      SetOurSupport((prevOurSupport) =>
        prevOurSupport.filter((ourSupport) => ourSupport._id !== ourSupportId)
      );
    } catch (error) {
      console.error("Error deleting testimonial:", error);
    }
  };

  return (
    <div>
      {/* Side PopUp */}
      <div
        ref={popUpRef}
        className={`bg-white fixed top-0 z-10 w-[400px] border-l ${
          isPopUpOpen ? "right-0" : "right-[-500px]"
        } h-full p-4 duration-300 space-y-4`}
      >
        <div className="space-y-2">
          <h1 className="font-primary text-title">Add Support</h1>
          <p className="text-black/[.60]">
            Add options of support you provide to your custormers
          </p>
        </div>

        <UploadImage ButtonText="Upload Icon" onUpload={handleImageUpload} />

        <TextInput
          Name="Name"
          Placeholder="Enter testimonial name"
          Value={inputValue}
          onChange={handleInputChange}
        />

        <TextAreaInput
          Name="Discription"
          Placeholder="Enter discription of support item"
          Value={descriptionValue}
          onChange={handleDescriptionChange}
        />

        <div className="flex items-center justify-end space-x-4">
          <p
            onClick={() => SetIsPopUpOpen(false)}
            className="text-black/[.60] cursor-pointer"
          >
            cancel
          </p>
          <div
            onClick={handleSubmit}
            className="bg-primary text-white hover:bg-primary_dark duration-300 p-2 px-4 rounded hover:cursor-pointer"
          >
            <p>{popUpLoading ? "Submiting..." : "Submit"}</p>
          </div>
        </div>
      </div>

      {/* Our Support Map */}
      <div className="grid grid-cols-4 gap-4">
        {loading
          ? Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} height={150} />
            ))
          : ourSupport.map((support) => (
              <div
                key={support.Name}
                className="flex items-start gap-4 bg-white p-2 py-3 border rounded group cursor-pointer"
              >
                <div className="flex items-start pt-2 h-full w-[15%]">
                  {/* Set a fixed width and height for the image */}
                  <img
                    className="w-full object-cover"
                    src={support.Icon}
                    alt="img"
                  />
                </div>
                <div className="w-[85%]">
                  <h1 className="font-primary capitalize">{support.Name}</h1>
                  <p className="text-black/[.60] text-[14px]">
                    {support.Description}
                  </p>
                </div>
                <div>
                  <MdOutlineDelete
                    onClick={() => handleDeleteOurSupport(support._id!)}
                    className={`text-[20px] group-hover:text-red-500 text-red-500/[.0] duration-300`}
                  />
                </div>
              </div>
            ))}
      </div>

      <div
        onClick={() => SetIsPopUpOpen(true)}
        className="bg-primary w-[46px] h-fit hover:w-[132px] p-3 text-[22px] text-white rounded-full fixed right-4 bottom-4 group duration-300 flex items-center gap-2 cursor-pointer"
      >
        <IoMdAdd />
        <p className="text-[16px] hidden h-[24px] pr-2 group-hover:flex duration-300">
          Add Iems
        </p>
      </div>
    </div>
  );
}
